/**
 * APIs here answer with a bare string on some routes and an object such as
 * { message, error } on others. Handing either straight to a toast or a Chip label
 * crashes React with "Objects are not valid as a React child", so everything that
 * displays a server payload must funnel through here first.
 */
const readable = (value, depth = 0) => {
  if (value === null || value === undefined || depth > 4) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value
      .map((v) => readable(v, depth + 1))
      .filter(Boolean)
      .join(', ');
  }
  if (typeof value === 'object') {
    // Most specific first, so { message, error } shows the human-facing message.
    const parts = [value.message, value.error, value.msg, value.detail, value.description]
      .map((v) => readable(v, depth + 1))
      .filter(Boolean);
    if (parts.length > 0) return [...new Set(parts)].join(' - ');
    try {
      const json = JSON.stringify(value);
      return json && json !== '{}' ? json : '';
    } catch (e) {
      return '';
    }
  }
  return '';
};

/** Readable text for a caught request error, whatever shape the server sent. */
export const getApiErrorMessage = (error, fallback = 'Something went wrong!') =>
  readable(error?.response?.data) ||
  readable(error?.message) ||
  // Only a plain string error is worth showing raw; dumping an unknown object's
  // JSON at the user is worse than the fallback sentence.
  (typeof error === 'string' ? error.trim() : '') ||
  fallback;

/** Readable text for a successful response body that may be a string or an object. */
export const getApiMessage = (data, fallback = 'Done!') => readable(data) || fallback;

export default getApiErrorMessage;
